'use client'

import Script from 'next/script'
import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
} from 'react'

import { type ClientSessionCustomerData } from '@redteclab/api/clients/bully'

import {
  FEATURE_TOGGLE_AD_SERVER,
  useFeatureToggle,
} from '../../../feature-toggles'
import { useGlobalConfigContext } from '../../../global-config'
import { useNextPathname } from '../../../next-pathname'
import { useRunCallbackWhenSessionCallIsDone } from '../../../user-data'
import {
  adServerInitCommandIsPushed,
  adServerSlotsGetElements,
  adServerSlotsPushInitCommand,
  adServerSlotsPushLoadCommand,
  adServerSlotsPushSyncUserCommand,
} from '../../adServerSlots'
import { type AdServerTargeting } from '../../getAdServerTargeting'

import {
  AdServerConfigurationContext,
  type AdServerConfigurationContextValue,
} from './AdServerConfigurationContext'
import { AdServerSlotsCleanup } from './AdServerSlotsCleanup'
import { useAdServerState } from './useAdServerState'

type AdServerConfigurationContextProviderProps = PropsWithChildren<
  Pick<AdServerConfigurationContextValue, 'adServerConfig'> & {
    adServerTargeting: AdServerTargeting
  }
>

export const AdServerConfigurationContextProvider: FC<
  AdServerConfigurationContextProviderProps
> = ({ adServerConfig, adServerTargeting, children }) => {
  const { language, tenant, tenantAndEnv } = useGlobalConfigContext()
  const { getBanner } = useAdServerState()
  const isAdServerEnabled = useFeatureToggle(FEATURE_TOGGLE_AD_SERVER)

  useRunCallbackWhenSessionCallIsDone(
    useCallback(
      (sessionData?: ClientSessionCustomerData) => {
        if (!isAdServerEnabled || !sessionData) {
          return
        }

        adServerSlotsPushSyncUserCommand(sessionData, tenant)
      },
      [isAdServerEnabled, tenant],
    ),
  )

  const pathname = useNextPathname()

  useEffect(() => {
    const isLandingPage = pathname.includes('/lp/')

    if (!isAdServerEnabled || isLandingPage || adServerInitCommandIsPushed()) {
      return
    }

    adServerSlotsPushInitCommand({
      language,
      tenantAndEnv,
    })

    const slots = adServerSlotsGetElements()

    if (slots.length === 0) {
      return
    }

    adServerSlotsPushLoadCommand(slots)
  }, [isAdServerEnabled, language, pathname, tenantAndEnv])

  const value = useMemo(
    () => ({
      adServerConfig,
      adServerTargeting,
      getBanner,
    }),
    [adServerConfig, adServerTargeting, getBanner],
  )

  return (
    <AdServerConfigurationContext.Provider value={value}>
      {/* Put `AdServerSlotsCleanup` first to clean up slots on `pathname` change before other `useEffect`'s take place */}
      <AdServerSlotsCleanup />
      {children}
      <Script src={adServerConfig.adServerClientSrc} />
    </AdServerConfigurationContext.Provider>
  )
}
